import React, { useCallback, useState } from 'react';

import { patch } from '@rails/request.js';

import { logError } from '../../lib/logger';
import { rootDomainUrl } from '../../lib/meta';

import AuthButton from '../auth/AuthButton';
import AuthField from '../auth/AuthField';
import AuthLayout from '../auth/AuthLayout';
import AvatarUploader from './AvatarUploader';

const FULL_NAME_FIELD = {
  name: 'full_name',
  label: 'Full name',
  type: 'text',
  placeholder: 'Full name',
  autoComplete: 'name',
  required: true,
};
const FORM_MESSAGE_ID = 'profile-setup-form-message';

export default function ProfileSetupApp({ user }) {
  const [fullName, setFullName] = useState('');
  const [formError, setFormError] = useState(null);
  const [avatarError, setAvatarError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = useCallback(function handleChangeCallback(event) {
    setFullName(event.target.value);
    setFormError(null);
  }, []);

  const handleAvatarErrorChange = useCallback(function handleAvatarErrorChangeCallback(message) {
    setAvatarError(message);
  }, []);

  const handleSubmit = useCallback(
    async function handleSubmitCallback(event) {
      event.preventDefault();
      const trimmedFullName = fullName.trim();
      if (!trimmedFullName) return;

      setIsSubmitting(true);
      setFormError(null);

      try {
        const response = await patch('/profile/setup', {
          body: { user: { full_name: trimmedFullName } },
          responseKind: 'json',
        });
        const data = await response.json;

        if (!response.ok || !data.ok) {
          const fullNameErrors = data?.validations?.full_name;
          const message = Array.isArray(fullNameErrors) && fullNameErrors.length > 0 ? `Full name ${fullNameErrors.join(', ')}` : 'Something went wrong. Please try again.';
          setFormError(message);
          return;
        }

        window.location.assign(data.data?.redirect_url || rootDomainUrl('/app/start'));
      } catch (error) {
        logError('ProfileSetupApp', 'Failed to save profile', { error });
        setFormError('Could not reach the server. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [fullName],
  );

  const continueDisabled = isSubmitting || fullName.trim().length === 0;
  const visibleError = formError || avatarError;

  return (
    <AuthLayout>
      <div className="flex w-full flex-col items-center gap-10">
        <div className="flex w-full flex-col items-center gap-2 text-center">
          <h1 className="w-full text-2xl leading-6 font-semibold tracking-[-0.48px] text-black">Your profile</h1>
          <p className="w-full text-sm leading-5 text-gray-500">So people can find you and know it’s you</p>
        </div>

        <AvatarUploader initialFlexicon={user?.flexicon ?? null} onErrorChange={handleAvatarErrorChange} />

        <form className="flex w-full flex-col gap-5" onSubmit={handleSubmit}>
          <AuthField
            disabled={isSubmitting}
            errorMessageId={FORM_MESSAGE_ID}
            field={FULL_NAME_FIELD}
            hasError={Boolean(formError)}
            onChange={handleChange}
            value={fullName}
          />

          <div className="flex w-full flex-col gap-2">
            {visibleError && (
              <p
                id={FORM_MESSAGE_ID}
                className="w-full px-1 text-sm leading-5 font-medium text-red-600"
                role="alert"
              >
                {visibleError}
              </p>
            )}
            <AuthButton disabled={continueDisabled}>{isSubmitting ? 'Saving...' : 'Continue'}</AuthButton>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
}
